import * as RadixTooltip from "@radix-ui/react-tooltip";
import classNames from "classnames";
import {
  MouseEventHandler,
  ReactElement,
  ReactNode,
  cloneElement,
  forwardRef,
  isValidElement,
} from "react";

export type IconButtonVariant = "default" | "primary" | "danger" | "ghost";

export type IconButtonSize = "sm" | "md" | "lg";

type LegacyIconButtonSize = "small" | "medium" | "large";

export type IconButtonTipPlacement = "top" | "bottom" | "left" | "right";

export type IconButtonDotTone = "danger" | "primary" | "secondary";

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The glyph. Pass a lucide-react element (e.g. `<Trash2 />`); the button
   * injects the size-appropriate `size` and `strokeWidth` unless the element
   * sets its own.
   */
  icon?: ReactNode;
  /**
   * Tooltip text, also used as the `aria-label`. An icon button has no
   * visible text, so every new call site must provide it. Optional only so
   * legacy call sites keep compiling.
   */
  label?: string;
  tipLabel?: string;
  /** Visual treatment. `ghost` is borderless, for dense toolbars and top strips. */
  variant?: IconButtonVariant;
  /**
   * `sm` 30px · `md` 36px · `lg` 44px. The legacy names
   * `small`/`medium`/`large` map to `sm`/`md`/`lg` and are deprecated.
   */
  size?: IconButtonSize | LegacyIconButtonSize;
  /** Tooltip placement relative to the button. */
  tip?: IconButtonTipPlacement;
  /** Suppress the tooltip; `label` still labels the button for screen readers. */
  noTip?: boolean;
  /** Toggle / selected state (`aria-pressed`), styled like the active nav item. */
  active?: boolean;
  /** Swap the glyph for a spinner and disable interaction (`aria-busy`). */
  loading?: boolean;
  /** Count bubble on the top-right corner; values above 99 render as "99+". */
  badge?: number | null;
  /** Small status dot on the top-right corner; hidden while a badge shows. */
  dot?: boolean;
  /** Dot color. */
  dotTone?: IconButtonDotTone;
  /** Stroke width injected into a lucide glyph. */
  strokeWidth?: number;
  /** @deprecated Pass the element via `icon` instead. */
  customIcon?: ReactNode;
  /** @deprecated Legacy class override; suppresses the variant palette. */
  color?: string;
  onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
}

const SIZE_ALIASES: Record<LegacyIconButtonSize, IconButtonSize> = {
  small: "sm",
  medium: "md",
  large: "lg",
};

const BUTTON_SIZE_CLASSES: Record<IconButtonSize, string> = {
  sm: "w-[30px] h-[30px]",
  md: "w-9 h-9",
  lg: "w-target h-target",
};

const GLYPH_SIZES: Record<IconButtonSize, number> = {
  sm: 15,
  md: 17,
  lg: 20,
};

const HOVER_BACKGROUND = /(^|\s)hover:bg-/;

const SPINNER_SIZE_CLASSES: Record<IconButtonSize, string> = {
  sm: "w-[13px] h-[13px]",
  md: "w-[15px] h-[15px]",
  lg: "w-[18px] h-[18px]",
};

const VARIANT_CLASSES: Record<IconButtonVariant, string> = {
  default: classNames(
    "bg-page border-default text-muted",
    "hover:bg-surface hover:border-default hover:text-default",
    "active:bg-surface active:border-default active:text-default",
  ),
  primary: classNames(
    "bg-page border-default text-primary",
    "hover:bg-tint hover:border-primary hover:text-primary",
    "active:bg-tint active:border-primary",
  ),
  danger: classNames(
    "bg-page border-default text-danger",
    "hover:bg-danger-subtle hover:border-danger",
    "active:bg-danger-subtle",
  ),
  ghost: classNames(
    "bg-transparent border-transparent text-muted",
    "hover:bg-surface hover:text-default",
    "active:bg-raised",
  ),
};

const ACTIVE_CLASSES = classNames(
  "bg-tint border-primary text-primary",
  "hover:bg-tint hover:border-primary",
);

const DOT_TONE_CLASSES: Record<IconButtonDotTone, string> = {
  danger: "bg-danger",
  primary: "bg-tint",
  secondary: "bg-warning",
};

function sizeGlyph(
  glyph: ReactNode,
  glyphSize: number,
  strokeWidth: number,
): ReactNode {
  if (!isValidElement(glyph) || typeof glyph.type === "string") {
    return glyph;
  }
  const element = glyph as ReactElement<{
    size?: number;
    strokeWidth?: number;
  }>;
  return cloneElement(element, {
    size: element.props.size ?? glyphSize,
    strokeWidth: element.props.strokeWidth ?? strokeWidth,
  });
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      icon,
      label,
      tipLabel,
      variant = "default",
      size = "md",
      tip = "top",
      noTip = false,
      active = false,
      loading = false,
      disabled = false,
      badge = null,
      dot = false,
      dotTone = "danger",
      strokeWidth = 2,
      customIcon,
      color,
      onClick,
      type = "button",
      className,
      ...props
    },
    ref,
  ) {
    const resolvedSize = SIZE_ALIASES[size as LegacyIconButtonSize] ?? size;
    const glyphSize = GLYPH_SIZES[resolvedSize];

    let glyph: ReactNode;
    if (loading) {
      glyph = (
        <span
          aria-hidden="true"
          className={classNames(
            "rounded-full border-2 border-current border-t-transparent",
            "opacity-85 motion-safe:animate-spin",
            SPINNER_SIZE_CLASSES[resolvedSize],
          )}
        />
      );
    } else {
      glyph = sizeGlyph(icon ?? customIcon, glyphSize, strokeWidth);
    }

    const badgeValue = typeof badge === "number" && badge !== 0 ? badge : null;
    const showBadge = badgeValue !== null;
    const isDisabled = disabled || loading;
    const tipText = tipLabel ?? label;
    const showTip = !noTip && !!tipText;

    const button = (
      <button
        ref={ref}
        type={type}
        onClick={onClick}
        aria-label={label}
        aria-pressed={active || undefined}
        aria-busy={loading || undefined}
        disabled={isDisabled}
        {...props}
        className={classNames(
          "relative hit-target shrink-0 grid place-items-center rounded border",
          "transition-colors duration-150 active:translate-y-px",
          "focus-visible:outline-none focus-visible:ring-2",
          "focus-visible:ring-focus focus-visible:border-focus",
          "disabled:pointer-events-none disabled:opacity-45",
          loading && "cursor-progress",
          BUTTON_SIZE_CLASSES[resolvedSize],
          color
            ? classNames(
                !HOVER_BACKGROUND.test(color) && "hover:bg-tint",
                color,
              )
            : active
              ? ACTIVE_CLASSES
              : VARIANT_CLASSES[variant],
          className,
        )}
      >
        {glyph}
        {showBadge && (
          <span
            className={classNames(
              "absolute -top-[5px] -right-[5px] z-[2]",
              "min-w-[16px] h-[16px] px-[4px]",
              "grid place-items-center rounded-full border-[1.5px] border-focus-inverse",
              "bg-danger text-on-primary text-[0.625rem] font-bold leading-none",
              "tabular-nums",
            )}
          >
            <span className="translate-y-[0.5px]">
              {badgeValue !== null && badgeValue > 99 ? "99+" : badgeValue}
            </span>
          </span>
        )}
        {!showBadge && dot && (
          <span
            aria-hidden="true"
            className={classNames(
              "absolute -top-px -right-px z-[2] w-2 h-2 rounded-full",
              "border-[1.5px] border-focus-inverse",
              DOT_TONE_CLASSES[dotTone],
            )}
          />
        )}
      </button>
    );

    if (!showTip) {
      return <span className="relative inline-flex">{button}</span>;
    }

    const tipDuplicatesName = tipText === label;

    return (
      <RadixTooltip.Provider delayDuration={300}>
        <RadixTooltip.Root>
          {tipDuplicatesName ? (
            <RadixTooltip.Trigger asChild>
              <span className="relative inline-flex">{button}</span>
            </RadixTooltip.Trigger>
          ) : (
            <span className="relative inline-flex">
              <RadixTooltip.Trigger asChild>{button}</RadixTooltip.Trigger>
            </span>
          )}
          <RadixTooltip.Portal>
            <RadixTooltip.Content
              side={tip}
              sideOffset={8}
              collisionPadding={8}
              className={classNames(
                "z-[60] pointer-events-none whitespace-nowrap",
                "bg-gray-800 text-on-primary text-xs font-medium leading-none",
                "px-2 py-1.5 rounded-md shadow-lg",
              )}
            >
              {tipText}
            </RadixTooltip.Content>
          </RadixTooltip.Portal>
        </RadixTooltip.Root>
      </RadixTooltip.Provider>
    );
  },
);
